"use client";

import { useEffect, useRef } from "react";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";
import { trackComponent } from "@repo/analytics/analytics";
import { dlEventConstants } from "@repo/analytics/data-layer.constants";
import { usePathname } from "next/navigation";
import { SEARCH_PLACEHOLDER_HI } from "../../constants/HeaderConstants";

const { IN_APP_REDIRECTION, BAJAJ_SUPER_APP, APP_SEARCH } = APP_PROMPTS;
const SEARCH_PROMPT = `${IN_APP_REDIRECTION}${BAJAJ_SUPER_APP}${APP_SEARCH}`;

type AppHeaderTarsier = {
  handleAppBack?: () => void;
  invokePrompt?: (prompt: string, flag: boolean) => void;
  readHistoryStack?: () => string[];
  writeHistoryStack?: (stack: string[]) => void;
};

type AppHeaderProps = {
  placeholder?: string;
  enableHindiPlaceholder?: boolean;
};

const AppHeader = ({ placeholder, enableHindiPlaceholder = false }: AppHeaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pathname = usePathname();
  const pathSegments = pathname?.split("/").filter(Boolean) ?? [];
  const routeLanguage = pathSegments[0]?.startsWith("app-v") ? pathSegments[1] : pathSegments[0];
  const resolvedPlaceholder =
    enableHindiPlaceholder && ["hi", "hindi"].includes(routeLanguage?.toLowerCase() ?? "")
      ? SEARCH_PLACEHOLDER_HI
      : placeholder;

  // Animated placeholder — typing effect matching 3in1cms animatePlaceholder
  useEffect(() => {
    const input = inputRef.current;
    if (!input || !resolvedPlaceholder) return;
    const originalPlaceholder = input.getAttribute("placeholder") || "";
    const text = resolvedPlaceholder;
    let i = 0;
    let timer: ReturnType<typeof setTimeout>;
    const type = () => {
      if (i < text.length) {
        input.setAttribute("placeholder", text.substring(0, i + 1));
        i++;
      } else {
        i = 0;
        input.setAttribute("placeholder", "");
      }
      timer = setTimeout(type, 175);
    };
    input.setAttribute("placeholder", "");
    type();
    return () => {
      clearTimeout(timer);
      input.setAttribute("placeholder", originalPlaceholder || text);
    };
  }, [resolvedPlaceholder]);

  // Back button — exact 3in1cms logic with analytics tracking
  const handleBack = async () => {
    if (typeof window === "undefined") return;
    const tarsier = window.tarsier as unknown as AppHeaderTarsier | undefined;
    const stack = tarsier?.readHistoryStack?.();
    if (stack && stack.length > 0) {
      const last = stack[stack.length - 1];
      if (
        last &&
        ((last.includes("/investments/fixed-deposit") && last.includes("appid")) ||
          (last.includes("/personal-loan") && last.includes("?referer=HomePage")))
      ) {
        stack.pop();
        // Normalize remaining entries to current env origin to avoid redirecting
        // to a hardcoded production hostname stored in the native history stack.
        const normalizedStack = stack.map((entry) => {
          try {
            const url = new URL(entry);
            return window.location.origin + url.pathname + url.search + url.hash;
          } catch {
            return entry;
          }
        });
        tarsier?.writeHistoryStack?.(normalizedStack);
      }
    }
    tarsier?.handleAppBack?.();

    // Analytics tracking — matches 3in1cms AppHeader pattern
    await trackComponent({
      componentName: "AppHeader",
      ctaText: dlEventConstants.BACK_BUTTON,
      entryPoint: dlEventConstants.DEVICE_BACK,
    });
  };

  // Search/voice click delegation — exact 3in1cms handleSearch logic
  const handleSearchClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>("[data-search]");
    if (!target) return;
    const tarsier = window.tarsier as unknown as AppHeaderTarsier | undefined;
    const type = target.dataset.search?.trim().toLowerCase();
    if (type === "text") {
      tarsier?.invokePrompt?.(`${SEARCH_PROMPT}/BAR`, true);
    } else if (type === "scan") {
      tarsier?.invokePrompt?.(`${SEARCH_PROMPT}/IMAGESEARCH`, true);
    } else if (type === "voice") {
      tarsier?.invokePrompt?.(`${SEARCH_PROMPT}/MIC`, true);
    }
  };

  return (
    <div
      role="none"
      className="bg-blue-header fixed top-0 left-0 z-10 flex w-full flex-row items-center justify-between p-2.5"
      data-testid="app-pdp-header"
      onClick={handleSearchClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ")
          handleSearchClick(e as unknown as React.MouseEvent<HTMLDivElement>);
      }}
    >
      {/* Back button */}
      <button
        type="button"
        className="bf-icon-arrow rotate-180 cursor-pointer border-none bg-transparent pl-2.5 text-2xl text-white"
        aria-label="Go back"
        data-testid="app-pdp-header-back"
        onClick={(e) => {
          e.stopPropagation();
          handleBack();
        }}
      />

      {/* Search bar */}
      <div
        data-search="text"
        className="flex flex-1 flex-row items-center justify-between rounded bg-white py-2.5 pr-4 pl-3"
      >
        <i
          data-search="text"
          className="bf-icon-search pr-3 text-[16px] text-[var(--color-tangerine-dark)]"
        />
        <input
          ref={inputRef}
          className="w-full border-0 bg-transparent text-sm text-gray-900 outline-none"
          type="text"
          placeholder={resolvedPlaceholder}
          readOnly
          aria-label="Search"
        />
        <i
          data-search="scan"
          className="bf-icon-scan pr-4 pl-3 text-[24px] text-[var(--color-grey-darken)]"
          aria-label="Capture search"
        />
        <i
          data-search="voice"
          className="bf-icon-hollow-mic text-[24px] text-[var(--color-grey-darken)]"
          aria-label="Voice search"
        />
      </div>
    </div>
  );
};

export default AppHeader;
